import { assetUrl } from '../config.js';
import { isInputLocked } from './inputLock.js';
import { shieldFromPhaser } from './uiShield.js';
import { iconHtml } from './iconHtml.mjs';
import { isTouchDevice } from './isTouchDevice.js';

// The instructor's strip along the top of TrainingRouteScene (Task 7's own thing to drive
// — this task only builds and exports it). A DOM overlay, same family as ChatInput's bar:
// full width, 72px tall, semi-opaque dark, her portrait cropped to a circle on the left,
// then a task icon, its title, and a right-aligned distance readout, with a small ✖ to
// bail out of the lesson/exam. Hidden while a modal is up (isInputLocked), same as every
// other DOM control that shares the canvas with Phaser's own input lock.
//
//   const strip = new InstructionStrip(scene, { portraitKey: 'npc_instructor', onAbort });
//   strip.announce(task, 480);   // "coming up in 480 m"
//   strip.progress(220);         // …just the distance, ticking down
//   strip.activate(task);        // the task is live now — no more distance
//   strip.coach('Stój i czekaj na zielone…');
//   strip.ok('Super, pełne zatrzymanie!');   // …auto-idles after 2s
//   strip.fail('Ojej — trzeba było się zatrzymać przed linią');
//   strip.idle();                 // "Jedź dalej 🙂" — the default resting state
//   strip.destroy();

const IDLE_ICON = '{face_smile}';
const IDLE_TEXT = 'Jedź dalej {face_smile}';
const OK_FAIL_MS = 2000;

export class InstructionStrip {
  constructor(scene, { portraitKey = 'npc_instructor', onAbort = null } = {}) {
    this.scene = scene;
    this._onAbort = onAbort;
    this._resolveTimer = null;

    const wrap = document.createElement('div');
    wrap.id = 'instruction-strip';
    wrap.style.cssText = [
      'position:fixed', 'top:0', 'left:0', 'right:0', 'height:72px', 'z-index:1550',
      'display:flex', 'align-items:center', 'gap:12px', 'padding:0 14px', 'box-sizing:border-box',
      'background:#0d0d14e6', 'color:#fff', 'font:16px system-ui',
    ].join(';');

    const portrait = document.createElement('img');
    portrait.src = assetUrl(`/assets/characters/${portraitKey}.webp`);
    portrait.alt = '';
    portrait.style.cssText = [
      'width:56px', 'height:56px', 'flex:0 0 auto', 'border-radius:50%',
      'object-fit:cover', 'object-position:top center', 'border:2px solid #ffd166',
    ].join(';');

    const icon = document.createElement('div');
    icon.style.cssText = 'flex:0 0 auto;width:32px;height:32px;line-height:32px;font-size:26px;text-align:center;';

    const title = document.createElement('div');
    title.style.cssText = [
      'flex:1 1 auto', 'min-width:0', 'font-size:24px', 'font-weight:bold',
      'overflow:hidden', 'text-overflow:ellipsis', 'white-space:nowrap',
    ].join(';');

    const distance = document.createElement('div');
    distance.style.cssText = 'flex:0 0 auto;min-width:70px;text-align:right;font-size:16px;color:#ffd166;';

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.title = 'Przerwij';
    closeBtn.style.cssText = [
      'flex:0 0 auto', 'width:30px', 'height:30px', 'border:none', 'border-radius:50%',
      'background:#ffffff22', 'color:#fff', 'font:14px system-ui', 'cursor:pointer',
    ].join(';');
    closeBtn.addEventListener('click', (e) => { e.preventDefault(); this._onAbort?.(); });

    // Phones: a slimmer strip (72 px was a fifth of a 340 px screen) that starts right of the
    // sound / music / gear column it used to cover and ends left of the chat chip, with a
    // finger-sized × to break off the lesson.
    if (isTouchDevice()) {
      Object.assign(wrap.style, { height: '52px', padding: '0 58px 0 58px', gap: '8px' });
      Object.assign(portrait.style, { width: '40px', height: '40px' });
      Object.assign(title.style, { fontSize: '18px' });
      Object.assign(closeBtn.style, { width: '44px', height: '44px', font: '22px system-ui' });
    }
    wrap.append(portrait, icon, title, distance, closeBtn);
    document.body.appendChild(wrap);
    shieldFromPhaser(wrap);

    this.el = wrap;
    this._icon = icon;
    this._title = title;
    this._distance = distance;

    // No lock/unlock event exists on the scene (inputLock.js is a plain counter) — polled
    // off the scene's own per-frame 'update' event, same cadence everything else already
    // reads isInputLocked() at.
    this._pollLock = () => {
      if (!this.el) return;
      this.el.style.display = isInputLocked(this.scene) ? 'none' : 'flex';
    };
    scene.events.on('update', this._pollLock);
    this._onShutdown = () => this.destroy();
    scene.events.once('shutdown', this._onShutdown);

    this.idle();
  }

  _clearTimer() {
    if (this._resolveTimer) { clearTimeout(this._resolveTimer); this._resolveTimer = null; }
  }

  _render(icon, text, distanceLabel = '') {
    this._clearTimer();
    if (this._icon) this._icon.innerHTML = iconHtml(icon ?? '');   // lesson icons are tokens ({sign_stop} …)
    if (this._title) this._title.innerHTML = iconHtml(text ?? '');
    if (this._distance) this._distance.textContent = distanceLabel;
  }

  static _distanceLabel(m) {
    if (m == null || !Number.isFinite(m)) return '';
    return `za ${Math.max(0, Math.round(m))} m`;
  }

  // A task coming up: its icon/title, with a "za N m" countdown.
  announce(task, distanceM) {
    this._render(task?.icon, task?.title, InstructionStrip._distanceLabel(distanceM));
  }

  // Just tick the distance down — same task, same title.
  progress(distanceM) {
    if (this._distance) this._distance.textContent = InstructionStrip._distanceLabel(distanceM);
  }

  // The task is live now (in its judged window) — same icon/title, no more distance.
  activate(task) {
    this._render(task?.icon, task?.title, '');
  }

  // A freeform coaching line — e.g. "Stój i czekaj na zielone…" from a `need` event.
  coach(text) {
    this._render('{speak}', text, '');
  }

  ok(text) {
    this._render('{check}', text, '');
    this._resolveTimer = setTimeout(() => this.idle(), OK_FAIL_MS);
  }

  fail(text) {
    this._render('{cross}', text, '');
    this._resolveTimer = setTimeout(() => this.idle(), OK_FAIL_MS);
  }

  idle() {
    this._render(IDLE_ICON, IDLE_TEXT, '');
  }

  destroy() {
    this._clearTimer();
    this.scene?.events?.off('update', this._pollLock);
    this.scene?.events?.off('shutdown', this._onShutdown);
    this.el?.remove();
    this.el = null;
  }
}
